import { memo, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';
import ComponentSuspense from '../../core/components/router/ComponentSuspense';
import LazyComponent from '../../core/components/router/LazyComponent';
import { AppContext } from '../../core/context';
import PreorderModeContext from '../../preorder/PreorderModeContext';
import type {
    LoggedInProductCustomerPrice,
    ProductCustomerPrice,
} from '../../product/types';
import type { PreorderPriceEditableProduct, ProductOptions } from '../type';
import { useCustomDiscountContext } from '../providers/CustomDiscountContext';
import PriceBreakdown from './PriceDisplaying/Breakdown/PriceBreakdown';

const PriceEditing = LazyComponent(
    async () => import('./PricingEditing/PriceEditing')
);

const isAnyProductOptionApplicable = (productOptions: ProductOptions) => {
    return (
        !!productOptions.sku ||
        !!productOptions.groupId ||
        !!productOptions.parentId ||
        !!productOptions.rebateCode
    );
};

const Price = ({
    children,
    product,
    customerPrice,
    retainPosition,
    horizontalEdit,
}: {
    children: ReactNode;
    product?: PreorderPriceEditableProduct;
    customerPrice?: ProductCustomerPrice;
    retainPosition?: boolean;
    horizontalEdit?: boolean;
}) => {
    const { isInPreorderMode } = useContext(PreorderModeContext);
    const { isImpersonating } = useContext(AppContext);
    const { editMode } = useCustomDiscountContext();

    const productOptions: ProductOptions | undefined = useMemo(() => {
        return product
            ? {
                  sku: product.sku || product.variantSku,
                  parentId:
                      product.parent?.id ||
                      (product.variants?.length ? product.id : undefined),
                  groupId: product.parent?.groupId || product.groupId,
                  rebateCode: product.rebateCode,
              }
            : undefined;
    }, [product]);

    const isBreakdown = isInPreorderMode && !isImpersonating && customerPrice;

    const isEditing = editMode && productOptions;

    if (isBreakdown) {
        return (
            <PriceBreakdown
                customerPrice={customerPrice as LoggedInProductCustomerPrice}
            >
                {children}
            </PriceBreakdown>
        );
    }

    if (isEditing) {
        if (!isAnyProductOptionApplicable(productOptions)) {
            return <>{children}</>;
        }

        return (
            <ComponentSuspense height="22px" small>
                <PriceEditing
                    productOptions={productOptions}
                    retainPosition={retainPosition}
                    horizontalEdit={horizontalEdit}
                >
                    {children}
                </PriceEditing>
            </ComponentSuspense>
        );
    }

    return <>{children}</>;
};

export default memo(Price);
